import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Card, Badge, LoadingState } from '../ui';
import { 
  ChatBubbleLeftRightIcon, 
  UserCircleIcon, 
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const ChainsManager = ({ projectId }) => {
  const [selectedChain, setSelectedChain] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const queryClient = useQueryClient();

  const { data: chainsData, isLoading: chainsLoading } = useQuery(
    ['chains', projectId, statusFilter],
    () => api.get(`/projects/${projectId}/chains`, { 
      params: { status: statusFilter || undefined, limit: 100 } 
    }).then(res => res.data),
    { enabled: !!projectId }
  );

  const { data: statsData } = useQuery(
    ['chainStats', projectId],
    () => api.get(`/projects/${projectId}/chains/stats`).then(res => res.data),
    { enabled: !!projectId }
  );

  const { data: chainDetails, isLoading: detailsLoading } = useQuery(
    ['chainDetails', selectedChain],
    () => api.get(`/chains/${selectedChain}`).then(res => res.data),
    { enabled: !!selectedChain }
  );

  const closeChainMutation = useMutation(
    (chainId) => api.post(`/chains/${chainId}/close`),
    {
      onSuccess: (_response, chainId) => {
        queryClient.invalidateQueries(['chains', projectId]);
        queryClient.invalidateQueries(['chainStats', projectId]);
        queryClient.invalidateQueries(['chainDetails', chainId]);
      }
    }
  );

  const chains = chainsData?.chains || [];
  const stats = statsData?.stats || {};

  if (chainsLoading) return <LoadingState message="Loading chains..." />;

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Chains"
          value={stats.total_chains || 0}
          icon={ChatBubbleLeftRightIcon}
        />
        <StatCard
          label="Active"
          value={stats.active_chains || 0}
          icon={ClockIcon}
          color="blue"
        />
        <StatCard
          label="Completed"
          value={stats.completed_chains || 0}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          label="Needs Review"
          value={stats.needs_review || 0}
          icon={ExclamationTriangleIcon}
          color="yellow"
        />
      </div>

      <div className="flex gap-4 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="manual_review">Needs Review</option>
        </select>
        <span className="text-gray-500 text-sm">
          {chains.length} chain{chains.length !== 1 ? 's' : ''} found
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Conversation Chains</h3>
          </div>
          <div className="max-h-[600px] overflow-y-auto divide-y divide-gray-100">
            {chains.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No chains found</p>
                <p className="text-sm mt-1">Chains appear when an agent replies in a tracked post</p>
              </div>
            ) : (
              chains.map((chain) => (
                <ChainRow
                  key={chain.id}
                  chain={chain}
                  isSelected={selectedChain === chain.id}
                  onClick={() => setSelectedChain(chain.id)}
                />
              ))
            )}
          </div>
        </Card>

        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Chain Details</h3>
          </div>
          {!selectedChain ? (
            <div className="p-8 text-center text-gray-500">
              <MagnifyingGlassIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Select a chain to view details</p>
            </div>
          ) : detailsLoading ? (
            <div className="p-8">
              <LoadingState message="Loading chain details..." />
            </div>
          ) : (
            <ChainDetail 
              chain={chainDetails?.chain} 
              messages={chainDetails?.messages || []}
              onClose={() => closeChainMutation.mutate(selectedChain)}
              isClosing={closeChainMutation.isLoading}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color = 'gray' }) => {
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
        </div>
      </div>
    </div>
  );
};

const ChainRow = ({ chain, isSelected, onClick }) => {
  const statusColors = {
    active: 'blue',
    completed: 'green',
    manual_review: 'yellow'
  };

  const statusLabels = {
    active: 'Active',
    completed: 'Completed',
    manual_review: 'Review'
  };

  return (
    <div
      onClick={onClick}
      className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={statusColors[chain.status] || 'gray'}>
              {statusLabels[chain.status] || chain.status}
            </Badge>
            <span className="text-xs text-gray-400">#{chain.id}</span>
          </div>
          <p className="text-sm font-medium text-gray-900 truncate">
            {chain.chat_title || `Chat ${chain.trigger_chat_id}`}
          </p>
          {chain.post_id && (
            <p className="text-xs text-gray-400 mt-0.5">Post #{chain.post_id}</p>
          )}
          <p className="text-sm text-gray-500 line-clamp-2 mt-1">
            {chain.trigger_message_text || 'Agent reply thread'}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="flex items-center gap-1 text-gray-500">
            <UserCircleIcon className="h-4 w-4" />
            <span className="text-sm">{chain.unique_agents_count || 0}</span>
          </div>
          <div className="flex items-center gap-1 text-gray-500 mt-1">
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            <span className="text-sm">{chain.total_agent_messages || 0}</span>
          </div>
        </div>
      </div>
      <div className="mt-2 text-xs text-gray-400">
        {chain.trigger_sender_username && `@${chain.trigger_sender_username} • `}
        {new Date(chain.trigger_at || chain.created_at).toLocaleString()}
      </div>
    </div>
  );
};

const ChainDetail = ({ chain, messages, onClose, isClosing }) => {
  const [expanded, setExpanded] = useState(true);

  if (!chain) return null;

  return (
    <div className="divide-y divide-gray-100">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium text-gray-900">Thread start</h4>
            {chain.post_id && (
              <p className="text-xs text-gray-500 mt-0.5">Discussion post #{chain.post_id}</p>
            )}
          </div>
          {chain.status === 'active' ? (
            <button
              type="button"
              onClick={onClose}
              disabled={isClosing}
              className="text-sm px-3 py-1.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              {isClosing ? 'Closing…' : 'Close chain'}
            </button>
          ) : (
            <Badge variant={chain.status === 'completed' ? 'green' : 'yellow'}>
              {chain.status === 'completed' ? 'Completed' : 'Needs review'}
            </Badge>
          )}
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
              <span className="text-purple-600 text-sm font-medium">
                {chain.trigger_sender_username?.[0]?.toUpperCase() || '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  @{chain.trigger_sender_username || 'unknown'}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(chain.trigger_at).toLocaleString()}
                </span>
              </div>
              <p className="text-gray-700 mt-1 whitespace-pre-wrap break-words">
                {chain.trigger_message_text || 'The replied-to message was not stored; agent replies are preserved below.'}
              </p>
              {chain.matched_keywords?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {chain.matched_keywords.map((kw, i) => (
                    <span key={i} className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                      {typeof kw === 'string' ? kw : kw.keyword}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          {chain.ended_at && (
            <p className="text-xs text-gray-500 mt-2">
              Ended {new Date(chain.ended_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>

      <div className="p-4">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 w-full text-left font-medium text-gray-900 mb-3"
        >
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
          Chain messages ({messages.length})
        </button>
        
        {expanded && (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {messages.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">No agent responses yet</p>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className="bg-blue-50 rounded-lg p-3 ml-4">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-sm font-medium">
                        {msg.username?.[0]?.toUpperCase() || 'A'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-blue-900">
                          @{msg.username}
                        </span>
                        {msg.first_name && (
                          <span className="text-blue-700 text-sm">
                            ({msg.first_name} {msg.last_name || ''})
                          </span>
                        )}
                        <span className="text-xs text-gray-400">
                          {new Date(msg.sent_at).toLocaleString()}
                        </span>
                      </div>
                      {msg.reply_to_username && (
                        <p className="text-xs text-blue-600 mt-0.5">
                          ↳ Reply to @{msg.reply_to_username}
                        </p>
                      )}
                      <p className="text-gray-700 mt-1 whitespace-pre-wrap break-words">
                        {msg.message_text || 'No message text'}
                      </p>
                      {msg.response_time_seconds && (
                        <p className="text-xs text-gray-400 mt-1">
                          Response time: {formatResponseTime(msg.response_time_seconds)}
                        </p>
                      )}
                      {msg.needs_review && (
                        <Badge variant="yellow" className="mt-2">Needs Review</Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const formatResponseTime = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

export default ChainsManager;
